import { useState, useEffect, useCallback } from 'react';
import { Card } from '@/propertyai/components/ui/card';
import { Input } from '@/propertyai/components/ui/input';
import { Label } from '@/propertyai/components/ui/label';
import { Separator } from '@/propertyai/components/ui/separator';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

export const MortgageCalc = ({ initialPrice = 400000, compact = false }) => {
  const [price, setPrice] = useState(initialPrice);
  const [downPayment, setDownPayment] = useState(Math.round(initialPrice * 0.2));
  const [rate, setRate] = useState(6.5);
  const [term, setTerm] = useState(30);
  const [taxRate, setTaxRate] = useState(1.2);
  const [insuranceRate, setInsuranceRate] = useState(0.5);

  useEffect(() => {
    setPrice(initialPrice);
    setDownPayment(Math.round(initialPrice * 0.2));
  }, [initialPrice]);

  const calculate = useCallback(() => {
    const loanAmount = price - downPayment;
    const monthlyRate = rate / 100 / 12;
    const numPayments = term * 12;

    let monthlyPI = 0;
    if (monthlyRate > 0) {
      monthlyPI = loanAmount * monthlyRate / (1 - Math.pow(1 + monthlyRate, -numPayments));
    } else {
      monthlyPI = loanAmount / numPayments;
    }

    const monthlyTax = (price * taxRate / 100) / 12;
    const monthlyInsurance = (price * insuranceRate / 100) / 12;
    const pmi = (downPayment / Math.max(price, 1)) < 0.2 ? (loanAmount * 0.005) / 12 : 0;

    return {
      principalInterest: Math.round(monthlyPI),
      tax: Math.round(monthlyTax),
      insurance: Math.round(monthlyInsurance),
      pmi: Math.round(pmi),
      total: Math.round(monthlyPI + monthlyTax + monthlyInsurance + pmi),
      loanAmount: Math.round(loanAmount),
    };
  }, [price, downPayment, rate, term, taxRate, insuranceRate]);

  const result = calculate();

  const pieData = [
    { name: 'Principal & Interest', value: result.principalInterest, color: 'hsl(186, 72%, 33%)' },
    { name: 'Property Tax', value: result.tax, color: 'hsl(222, 47%, 30%)' },
    { name: 'Insurance', value: result.insurance, color: 'hsl(34, 70%, 55%)' },
    ...(result.pmi > 0 ? [{ name: 'PMI', value: result.pmi, color: 'hsl(160, 45%, 38%)' }] : []),
  ];

  return (
    <Card className="rounded-2xl border border-border bg-background p-5" data-testid="mortgage-calculator">
      <h3 className="font-semibold mb-4">Mortgage Calculator</h3>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <div>
          <Label className="text-xs text-muted-foreground">Home Price</Label>
          <Input
            type="number"
            value={price}
            onChange={(e) => setPrice(Number(e.target.value))}
            className="mt-1 tabular-nums"
            data-testid="mortgage-price-input"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Down Payment</Label>
          <Input
            type="number"
            value={downPayment}
            onChange={(e) => setDownPayment(Number(e.target.value))}
            className="mt-1 tabular-nums"
            data-testid="mortgage-down-payment-input"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Interest Rate (%)</Label>
          <Input
            type="number"
            step="0.1"
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            className="mt-1 tabular-nums"
            data-testid="mortgage-interest-rate-input"
          />
        </div>
        <div>
          <Label className="text-xs text-muted-foreground">Loan Term (years)</Label>
          <Input
            type="number"
            value={term}
            onChange={(e) => setTerm(Number(e.target.value))}
            className="mt-1 tabular-nums"
          />
        </div>
      </div>

      <Separator className="my-4" />

      {/* Results */}
      <div className="text-center mb-4">
        <p className="text-xs text-muted-foreground">Estimated Monthly Payment</p>
        <p className="text-3xl font-semibold tabular-nums font-['Space_Grotesk'] mt-1" data-testid="mortgage-monthly-payment">
          ${result.total.toLocaleString()}
        </p>
      </div>

      {!compact && (
        <div className="flex gap-4">
          {/* Pie chart */}
          <div className="w-32 h-32 flex-shrink-0">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={25} outerRadius={50} dataKey="value" stroke="none">
                  {pieData.map((entry, i) => (
                    <Cell key={i} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip formatter={(v) => `$${v.toLocaleString()}`} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown */}
          <div className="flex-1 space-y-2">
            {pieData.map((item, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2">
                  <div className="w-2.5 h-2.5 rounded-full" style={{ background: item.color }} />
                  <span className="text-muted-foreground text-xs">{item.name}</span>
                </div>
                <span className="font-medium tabular-nums text-xs">${item.value.toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};

export default MortgageCalc;

